package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.sections.WaitingForLink
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.state.Identity
import com.htmlagent.site.theme.EnterCardStyle
import com.htmlagent.site.theme.EnterCloseStyle
import com.htmlagent.site.theme.EnterLeadStyle
import com.htmlagent.site.theme.EnterScrimStyle
import com.htmlagent.site.theme.EnterTitleStyle
import com.htmlagent.site.theme.ErrorStyle
import com.htmlagent.site.theme.GateFormStyle
import com.htmlagent.site.theme.GateGuestVariant
import com.htmlagent.site.theme.GateInputVariant
import com.htmlagent.site.theme.GateLabelStyle
import com.htmlagent.site.theme.GatePrimaryVariant
import com.htmlagent.site.theme.GateWarningStyle
import com.htmlagent.site.theme.GateWhyDetailStyle
import com.htmlagent.site.theme.GateWhyStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.labelled
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.TextInput
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button as DomButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/** Ties the visible label to the field. Only one of these opens at a time. */
private const val EMAIL_FIELD_ID = "enter-email"

/** What `aria-labelledby` points at, so the dialog announces itself by its own title. */
private const val TITLE_ID = "enter-title"

/**
 * The way in for whoever is not signed in, over the landing instead of on a page of its own:
 * every "start" button opens it, so nobody loses the place they were reading.
 *
 * It asks for an email to send the access link to, or lets the person go on as a guest. Once
 * the link is on its way the card says where it went and shows the [match] number, in place of
 * the form; whoever opened the dialog signs in once the link is opened. [error] is what the
 * API answered, already in words; the email check is [Identity]'s, the same the gate in `/app`
 * uses. The workspace opens it too, with its own [title] and [lead] and no [onGuest], when a
 * guest runs out of turns.
 */
@Composable
@Suppress("LongParameterList") // The dialog's states come from the page: values, not dependencies.
fun EnterDialog(
    visible: Boolean,
    onEnter: (String) -> Unit,
    onGuest: (() -> Unit)?,
    onClose: () -> Unit,
    sending: Boolean = false,
    error: String? = null,
    sentTo: String? = null,
    match: Int? = null,
    onOtherEmail: () -> Unit = {},
    title: String = LocalStrings.current.landing.enterTitle,
    lead: String = LocalStrings.current.landing.enterLead,
) {
    val t = LocalStrings.current
    var draft by remember { mutableStateOf("") }
    var typingError by remember { mutableStateOf<String?>(null) }
    var explained by remember { mutableStateOf(false) }
    val shownError = typingError ?: error

    // Wrapped in an `if` and not guarded by an early return: a `@Composable` that returns early
    // produces nothing, and goes on producing nothing after the state flips.
    if (visible) {
        DisposableEffect(Unit) {
            val onKey: (Event) -> Unit = { event ->
                if ((event as? KeyboardEvent)?.key == "Escape") onClose()
            }
            document.addEventListener("keydown", onKey)
            onDispose { document.removeEventListener("keydown", onKey) }
        }

        // Focus goes to the field and comes back to whatever opened the dialog, which is the
        // half of a modal a keyboard notices. Not on a touch screen: focusing the field there
        // raises the keyboard over the card before it has been read.
        DisposableEffect(Unit) {
            val opener = document.activeElement as? HTMLElement
            if (window.matchMedia("(pointer: fine)").matches) {
                (document.getElementById(EMAIL_FIELD_ID) as? HTMLElement)?.focus()
            }
            onDispose { opener?.focus() }
        }

        fun submit() {
            val value = draft.trim()
            if (Identity.looksLikeEmail(value)) {
                typingError = null
                onEnter(value)
            } else {
                typingError = t.gate.invalidEmail
            }
        }

        Div(EnterScrimStyle.toModifier().onClick { onClose() }.toAttrs()) {
            Div(
                EnterCardStyle.toModifier()
                    // The scrim closes; the card is not the scrim.
                    .onClick { it.stopPropagation() }
                    .toAttrs {
                        attr("role", "dialog")
                        attr("aria-modal", "true")
                        attr("aria-labelledby", TITLE_ID)
                    },
            ) {
                DomButton(
                    attrs = EnterCloseStyle.toModifier()
                        .labelled(t.landing.enterClose)
                        .toAttrs {
                            attr("type", "button")
                            onClick { onClose() }
                        },
                ) {
                    Glyph(Icons.Close)
                }

                Div {
                    H2(EnterTitleStyle.toModifier().toAttrs { attr("id", TITLE_ID) }) {
                        Text(title)
                    }
                    P(EnterLeadStyle.toModifier().toAttrs()) { Text(lead) }
                }

                if (sentTo != null && match != null) {
                    WaitingForLink(sentTo, match, onOtherEmail)
                } else Form(
                    attrs = GateFormStyle.toModifier().toAttrs {
                        // With `type="email"` the browser blocks the submit silently and our own
                        // message, which says what is actually wrong, would never run.
                        attr("novalidate", "")

                        // The `Form` DSL exposes no `onSubmit`; this is what makes Enter submit.
                        ref { form ->
                            val listener: (Event) -> Unit = { event ->
                                event.preventDefault()
                                submit()
                            }
                            form.addEventListener("submit", listener)
                            onDispose { form.removeEventListener("submit", listener) }
                        }
                    },
                ) {
                    Label(forId = EMAIL_FIELD_ID, attrs = GateLabelStyle.toModifier().toAttrs()) {
                        Text(t.gate.emailLabel)
                    }
                    TextInput(
                        text = draft,
                        onTextChange = { draft = it },
                        modifier = Modifier.attrsModifier {
                            attr("id", EMAIL_FIELD_ID)
                            attr("type", "email")
                            attr("autocomplete", "email")
                            if (shownError != null) attr("aria-invalid", "true")
                        },
                        variant = GateInputVariant,
                        placeholder = t.gate.emailPlaceholder,
                        focusBorderColor = null,
                    )

                    // The border color cannot be the only thing that says the field is wrong.
                    shownError?.let { message ->
                        Div(ErrorStyle.toModifier().toAttrs { attr("role", "alert") }) { Text(message) }
                    }

                    Button(
                        onClick = { submit() },
                        enabled = !sending,
                        modifier = Modifier.attrsModifier { attr("type", "submit") },
                        variant = GatePrimaryVariant,
                    ) {
                        Text(if (sending) t.gate.sending else t.gate.submit)
                    }
                }

                // Second and quieter: nobody should have to say who they are to see this work.
                if (onGuest != null) {
                    Button(
                        onClick = { onGuest() },
                        modifier = Modifier.attrsModifier { attr("type", "button") },
                        variant = GateGuestVariant,
                    ) {
                        Text(t.gate.guestSubmit)
                    }
                }

                // No password: this line says what the email is for, and the detail says why.
                Div(GateWarningStyle.toModifier().toAttrs()) {
                    Text(t.gate.shortNotice + " ")
                    // A bare `<button>` and not Silk's: that one keeps a box of its own and the
                    // link drops to a line by itself.
                    DomButton(
                        attrs = GateWhyStyle.toModifier().toAttrs {
                            attr("type", "button")
                            attr("aria-expanded", explained.toString())
                            onClick { explained = !explained }
                        },
                    ) {
                        Text(t.gate.why)
                    }
                    if (explained) {
                        Div(GateWhyDetailStyle.toModifier().toAttrs()) { Text(t.gate.notALoginNotice) }
                    }
                }
            }
        }
    }
}
